import type { PortfolioStatementImportV2, StatementImportOperationStatusV2,
  StatementImportUploadResponseV2 } from "@tradvera/contracts";
import type { StatementImportClientV2 } from "./client";
import { statementImportWriteOutcomeUnknownV2 } from "./client";
import { finalizePendingStatementOperationV2, persistPendingStatementOperationV2,
  readPendingStatementOperationV2, samePendingStatementOperationV2,
  withPendingStatementOperationLockV2,
  type PendingStatementOperationV2 } from "./pending-operation";
import type { PendingStatementUploadV2 } from "./pending-upload";
import { prepareBrowserStatementUploadV2, type StatementUploadChoiceV2 } from "./upload";

export interface DurableStatementUploadStorageV2 extends
  Pick<Storage, "getItem" | "setItem" | "removeItem"> {}

export async function executeDurableStatementUploadV2(options: {
  client: Pick<StatementImportClientV2, "upload">;
  storage: DurableStatementUploadStorageV2;
  ownerId: string;
  bookId: string;
  file: File;
  choice: StatementUploadChoiceV2;
  signal?: AbortSignal;
}): Promise<StatementImportUploadResponseV2> {
  return withPendingStatementOperationLockV2(options.ownerId, options.bookId, async () => {
  const pending = readPendingStatementOperationV2(options.storage, options.ownerId, options.bookId);
  if (pending.state === "INVALID" || pending.state === "UNAVAILABLE") pendingError(pending.state);
  if (pending.state === "VALID" && pending.marker.operation !== "UPLOAD") pendingError("OTHER_OPERATION");
  const prepared = await prepareBrowserStatementUploadV2({ file: options.file, choice: options.choice,
    bookId: options.bookId, idempotencyKey: pending.state === "VALID" ? pending.marker.idempotencyKey : undefined });
  const marker: PendingStatementOperationV2 = { operation: "UPLOAD",
    bookId: options.bookId, importId: null, targetReviewRevision: null,
    idempotencyKey: prepared.idempotencyKey,
    inputFingerprint: prepared.inputFingerprint,
    createdAtEpochMs: pending.state === "VALID" ? pending.marker.createdAtEpochMs : Date.now() };
  if (pending.state === "VALID" && !samePendingStatementOperationV2(pending.marker, marker)) {
    pendingError("INPUT_MISMATCH");
  }
  if (pending.state === "ABSENT") persistPendingStatementOperationV2(options.storage, options.ownerId, marker);
  try {
    const response = await options.client.upload(prepared, options.signal);
    requireFinalized(options.storage, options.ownerId, marker); return response;
  } catch (error) {
    if (!statementImportWriteOutcomeUnknownV2(error)) requireFinalized(options.storage, options.ownerId, marker);
    throw error;
  }
  });
}

export type StatementUploadRecoveryV2 =
  | { state: "ABSENT" }
  | { state: "LOCKED"; code: string; marker?: PendingStatementUploadV2 }
  | { state: "RETRY_EXACT_FILE"; marker: PendingStatementUploadV2; status: StatementImportOperationStatusV2 }
  | { state: "PENDING"; marker: PendingStatementUploadV2; status: StatementImportOperationStatusV2 }
  | { state: "RECOVERED"; import: PortfolioStatementImportV2; status: StatementImportOperationStatusV2 };

export async function recoverPendingStatementUploadV2(options: {
  client: Pick<StatementImportClientV2, "operationStatus" | "detail">;
  storage: DurableStatementUploadStorageV2;
  ownerId: string;
  bookId: string;
  signal?: AbortSignal;
}): Promise<StatementUploadRecoveryV2> {
  return withPendingStatementOperationLockV2(options.ownerId, options.bookId, async () => {
  const pending = readPendingStatementOperationV2(options.storage, options.ownerId, options.bookId);
  if (pending.state === "ABSENT") return pending;
  if (pending.state !== "VALID") return { state: "LOCKED", code: `STATEMENT_IMPORT_PENDING_OPERATION_${pending.state}` };
  if (pending.marker.operation !== "UPLOAD") return { state: "LOCKED",
    code: "STATEMENT_IMPORT_PENDING_OPERATION_OTHER_OPERATION" };
  const marker = pending.marker; const publicMarker = uploadMarker(marker);
  let status: StatementImportOperationStatusV2;
  try {
    status = await options.client.operationStatus({ operation: "UPLOAD", importId: null,
      idempotencyKey: marker.idempotencyKey, inputFingerprint: marker.inputFingerprint,
      dispositionCounts: null }, options.signal);
  } catch (error) {
    return { state: "LOCKED", code: error instanceof Error ? error.message
      : "STATEMENT_IMPORT_OUTCOME_UNKNOWN", marker: publicMarker };
  }
  if (status.state === "NOT_FOUND") return { state: "RETRY_EXACT_FILE", marker: publicMarker, status };
  if (status.state === "CONFLICT" || status.state === "OUTCOME_UNKNOWN") {
    return { state: "LOCKED", code: status.state, marker: publicMarker };
  }
  if ((status.state === "PENDING" || status.state === "COMMITTED") && status.importId) {
    try {
      const imported = await options.client.detail(status.importId, options.signal);
      // operationStatus is bound to the exact immutable UPLOAD_ACCEPTED event.  The import
      // detail intentionally exposes its newest upload event and may therefore carry a newer
      // idempotency key after a same-file/raw-SHA replay.  Scope and import identity remain
      // mandatory, but the mutable latest-event projection must not invalidate older truth.
      if (imported.bookId !== marker.bookId || imported.importId !== status.importId) return { state: "LOCKED",
        code: "STATEMENT_IMPORT_STATUS_BINDING_MISMATCH", marker: publicMarker };
      if (!finalized(options.storage, options.ownerId, marker)) return { state: "LOCKED",
        code: "STATEMENT_IMPORT_PENDING_OPERATION_REPLACED", marker: publicMarker };
      return { state: "RECOVERED", import: imported, status };
    } catch (error) {
      return { state: "LOCKED", code: error instanceof Error ? error.message
        : "STATEMENT_IMPORT_OUTCOME_UNKNOWN", marker: publicMarker };
    }
  }
  return { state: "PENDING", marker: publicMarker, status };
  });
}

function requireFinalized(storage: DurableStatementUploadStorageV2, ownerId: string,
  marker: PendingStatementOperationV2): void {
  const result = finalizePendingStatementOperationV2(storage, ownerId, marker);
  if (result.state !== "ABSENT") pendingError(result.state === "REPLACED" ? "REPLACED" : result.state);
}
function finalized(storage: DurableStatementUploadStorageV2, ownerId: string,
  marker: PendingStatementOperationV2): boolean {
  return finalizePendingStatementOperationV2(storage, ownerId, marker).state === "ABSENT";
}
function uploadMarker(marker: PendingStatementOperationV2): PendingStatementUploadV2 {
  return { operation: "UPLOAD", idempotencyKey: marker.idempotencyKey,
    inputFingerprint: marker.inputFingerprint, bookId: marker.bookId };
}
function pendingError(reason: string): never {
  throw new Error(`STATEMENT_IMPORT_PENDING_OPERATION_${reason}`);
}
